import ReactECharts from 'echarts-for-react';
import matrixData from '../../data/outreachCompetitionMatrix.json';
import { useIsDark } from '../../useIsDark';

/**
 * OutreachCompetitionMatrix — Raisy vs investor-outreach competition (blog15, Fig 1).
 *
 * Chart-type-first: two-axis positioning of named entities → ECharts scatter.
 * Data reads from outreachCompetitionMatrix.json (figures.competition_matrix),
 * Raisy's own documented competitive assessment, not an independent benchmark
 * (disclosed in the caption and the post's Sources footer). Raisy is the only
 * teal point; every competitor is neutral grey — identity is never color-alone,
 * every point is labeled. Theme: `dark` prop override, otherwise follows the
 * live html class theme (Rule 29). ONE shared
 * formatter for axis/tooltip/label consistency (Rule 21).
 */

const BRAND = {
  teal: '#0AB3BF',
  muted: '#64748B',
};

type MatrixItem = { name: string; x: number; y: number; quadrant: string; note: string; isRaisy?: boolean };

const fmtAxis = (v: number) => `${v}`;

export const OutreachCompetitionMatrix = ({
  height = 440,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const fig = (
    matrixData as unknown as {
      figures: {
        competition_matrix: {
          source: string;
          axes: { x: string; y: string };
          items: MatrixItem[];
        };
      };
    }
  ).figures.competition_matrix;

  const C = isDark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { data: MatrixItem }) =>
        `<b>${p.data.name}</b><br/>Execution: ${fmtAxis(p.data.x)} &middot; Fundraising-specific: ${fmtAxis(
          p.data.y
        )}<br/><span style="color:${C.muted}">${p.data.note}</span>`,
    },
    grid: { left: 56, right: 32, top: 32, bottom: 56 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 10,
      name: fig.axes.x,
      nameLocation: 'middle',
      nameGap: 32,
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 11, formatter: fmtAxis },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.25 } },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 10,
      name: fig.axes.y,
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 11, formatter: fmtAxis },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.25 } },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: (val: number[], p: { data: MatrixItem }) => (p.data.isRaisy ? 22 : 14),
        data: fig.items.map((i) => ({
          name: i.name,
          value: [i.x, i.y],
          x: i.x,
          y: i.y,
          note: i.note,
          isRaisy: i.isRaisy,
          itemStyle: {
            color: i.isRaisy ? BRAND.teal : BRAND.muted,
            borderColor: i.isRaisy ? '#087B8B' : '#475569',
            borderWidth: i.isRaisy ? 2 : 1,
          },
        })),
        label: {
          show: true,
          formatter: (p: { data: MatrixItem }) => p.data.name,
          // SendVC/Articuler share the same (x, y) coordinate; nudge them apart
          // so labels don't overlap (read-as-a-reader check, RULES_BLOG_POST).
          position: (p: { data: MatrixItem }) =>
            p.data.name === 'SendVC' ? 'left' : p.data.name === 'Articuler' ? 'right' : 'top',
          color: C.axis,
          fontSize: 11,
          fontWeight: (p: { data: MatrixItem }) => (p?.data?.isRaisy ? 700 : 400),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center px-4`}>
        Figure 1. Execution breadth vs fundraising specificity. {fig.source}.
      </p>
    </div>
  );
};
